import { Grid, useTheme, alpha } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import { AssignmentTurnedIn as AssignmentIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { MetricsData } from '@/types';
import { themeConstants } from '@/theme';
import {
    MetricsStatCard,
    MetricsChartCard,
    MetricsSectionHeading,
    makeTooltipStyle,
} from './index';

interface Props {
    data: MetricsData;
}

export const MetricsAssignmentsSection = ({ data }: Props) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const tt = makeTooltipStyle(theme);
    const divider = alpha(theme.palette.divider, 0.5);
    const tickStyle = { fill: theme.palette.text.secondary, fontSize: 12 };
    const isFiltered = data.resourceId !== null;
    const filteredLabel = LL.metricsPage.filteredByUser();

    const assignmentsPerMonth = useMemo(
        () =>
            data.assignmentsPerMonth.map(({ month, count, crCreated, crCancelled, crAccepted }) => {
                const [y, m] = month.split('-');
                return {
                    month: new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, {
                        month: 'short',
                        year: '2-digit',
                    }),
                    count,
                    crCreated: crCreated ?? 0,
                    crCancelled: crCancelled ?? 0,
                    crAccepted: crAccepted ?? 0,
                };
            }),
        [data.assignmentsPerMonth]
    );

    return (
        <MetricsSectionHeading
            icon={<AssignmentIcon fontSize="small" />}
            title={LL.metricsPage.sections.assignments()}
        >
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.totalAssignments()}
                        value={data.assignments.total}
                        icon={<AssignmentIcon />}
                        color="primary.main"
                        gradient={themeConstants.gradients.cardPrimary}
                        border={themeConstants.borders.primary}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.assignmentsInRange()}
                        value={data.assignments.inRange}
                        icon={<AssignmentIcon />}
                        color="success.main"
                        gradient={themeConstants.gradients.cardSuccess}
                        border={themeConstants.borders.success}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricsStatCard
                        label={LL.metricsPage.stats.openChangeRequests()}
                        value={data.assignments.openChangeRequests}
                        icon={<AssignmentIcon />}
                        color="warning.main"
                        gradient={themeConstants.gradients.cardWarning}
                        border={themeConstants.borders.warning}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    />
                </Grid>

                {/* Assignments over time */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.assignmentsPerMonth()}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={assignmentsPerMonth}
                                margin={{ left: 0, right: 16, top: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis dataKey="month" tick={tickStyle} />
                                <YAxis allowDecimals={false} tick={tickStyle} />
                                <Tooltip {...tt} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={{ r: 4, fill: theme.palette.primary.main }}
                                    activeDot={{ r: 6 }}
                                    name={LL.metricsPage.charts.assignmentsLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crCreated"
                                    stroke={theme.palette.warning.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsCreatedLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crCancelled"
                                    stroke={theme.palette.error.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsCancelledLabel()}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="crAccepted"
                                    stroke={theme.palette.success.main}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    name={LL.metricsPage.charts.changeRequestsAcceptedLabel()}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>

                {/* Top categories */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <MetricsChartCard
                        title={LL.metricsPage.charts.topCategoriesByAssignments()}
                        height={260}
                        filtered={isFiltered}
                        filteredLabel={filteredLabel}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.assignmentsPerCategory}
                                layout="vertical"
                                margin={{ left: 8, right: 16 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={divider} />
                                <XAxis
                                    type="number"
                                    allowDecimals={false}
                                    tick={{ ...tickStyle, fontSize: 11 }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    tick={{ ...tickStyle, fontSize: 11 }}
                                    width={100}
                                />
                                <Tooltip {...tt} />
                                <Bar
                                    dataKey="count"
                                    fill={theme.palette.warning.main}
                                    radius={[0, 4, 4, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </MetricsChartCard>
                </Grid>
            </Grid>
        </MetricsSectionHeading>
    );
};
